import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    ArrowRight,
    CheckCircle2,
    Circle,
    FileText,
    GitBranch,
    Package,
    Receipt,
    ShoppingCart,
    Truck,
} from 'lucide-react';
import { useGetList } from 'ra-core';
import { Link } from 'react-router-dom';

interface PipelineStage {
    label: string;
    reference: string | null;
    status: string | null;
    href: string | null;
    icon: React.ElementType;
    color: string;
}

interface ProjectPipeline {
    projectId: string | number;
    projectName: string;
    stages: PipelineStage[];
}

const statusColors: Record<string, string> = {
    draft: 'bg-muted text-muted-foreground border-border',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    converted: 'bg-purple-50 text-purple-700 border-purple-200',
    paid: 'bg-green-50 text-green-700 border-green-200',
    cancelled: 'bg-red-50 text-red-600 border-red-200',
    overdue: 'bg-orange-50 text-orange-700 border-orange-200',
};

const StageNode = ({ stage }: { stage: PipelineStage }) => {
    const Icon = stage.icon;
    const hasDoc = !!stage.reference;
    const colorClass = stage.status ? (statusColors[stage.status] ?? 'bg-muted text-muted-foreground') : '';

    return (
        <div className="flex flex-col items-center gap-1.5 min-w-[110px]">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                hasDoc
                    ? 'border-emerald-400 bg-emerald-50 shadow-sm'
                    : 'border-border bg-muted opacity-50'
            }`}>
                {hasDoc
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    : <Icon className="w-5 h-5 text-slate-300" />
                }
            </div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider text-center">{stage.label}</span>
            {hasDoc && stage.href ? (
                <Link to={stage.href} className="text-[11px] font-mono font-bold text-blue-600 hover:underline text-center leading-tight">
                    {stage.reference}
                </Link>
            ) : (
                <span className="text-[10px] text-slate-300 italic">—</span>
            )}
            {stage.status && (
                <Badge variant="outline" className={`text-[9px] font-bold px-1.5 py-0 ${colorClass}`}>
                    {stage.status}
                </Badge>
            )}
        </div>
    );
};

const Connector = ({ filled }: { filled: boolean }) => (
    <div className="flex items-center self-start mt-4">
        <ArrowRight className={`w-5 h-5 ${filled ? 'text-emerald-400' : 'text-slate-200'}`} />
    </div>
);

/**
 * DocumentPipelineView
 *
 * Renders a visual pipeline for each project showing:
 * Quotation → Proforma Invoice → Sales Order → Tax Invoice
 *
 * Uses ReferenceManyField-style data fetching per project.
 */
export const DocumentPipelineView = () => {
    const { data: projects, isPending: loadingProjects } = useGetList('projects', {
        pagination: { page: 1, perPage: 100 },
        filter: { status: 'active' },
        sort: { field: 'name', order: 'ASC' },
    });
    const { data: quotations, isPending: loadingQ } = useGetList('quotations', {
        pagination: { page: 1, perPage: 1000 },
        filter: {},
    });
    const { data: proformaInvoices, isPending: loadingPI } = useGetList('proforma_invoices', {
        pagination: { page: 1, perPage: 1000 },
        filter: {},
    });
    const { data: salesOrders, isPending: loadingSO } = useGetList('sales_orders', {
        pagination: { page: 1, perPage: 1000 },
        filter: {},
    });
    const { data: invoices, isPending: loadingInv } = useGetList('customer_invoices', {
        pagination: { page: 1, perPage: 1000 },
        filter: {},
    });

    const isPending = loadingProjects || loadingQ || loadingPI || loadingSO || loadingInv;

    if (isPending) {
        return (
            <Card className="border-none shadow-sm">
                <CardContent className="flex items-center justify-center h-48">
                    <div className="text-muted-foreground text-sm animate-pulse flex items-center gap-2">
                        <GitBranch className="w-4 h-4" />
                        Building pipeline…
                    </div>
                </CardContent>
            </Card>
        );
    }

    const pipelines: ProjectPipeline[] = (projects ?? []).map((project) => {
        const projectId = project.id;

        const quotation = (quotations ?? []).find((q) => q.project_id === projectId);
        const pi = (proformaInvoices ?? []).find(
            (p) => p.project_id === projectId || (quotation && p.quotation_id === quotation.id)
        );
        const so = (salesOrders ?? []).find(
            (s) => s.project_id === projectId || (quotation && s.quotation_id === quotation.id)
        );
        const invoice = (invoices ?? []).find(
            (i) => i.project_id === projectId || (pi && i.proforma_invoice_id === pi.id)
        );

        const stages: PipelineStage[] = [
            {
                label: 'Quotation',
                reference: quotation?.reference ?? null,
                status: quotation?.status ?? null,
                href: quotation ? `/quotations/${quotation.id}/show` : null,
                icon: FileText,
                color: 'blue',
            },
            {
                label: 'Proforma Inv.',
                reference: pi?.reference ?? null,
                status: pi?.status ?? null,
                href: pi ? `/proforma_invoices/${pi.id}/show` : null,
                icon: Receipt,
                color: 'purple',
            },
            {
                label: 'Sales Order',
                reference: so?.reference ?? null,
                status: so?.status ?? null,
                href: so ? `/sales_orders/${so.id}/show` : null,
                icon: ShoppingCart,
                color: 'emerald',
            },
            {
                label: 'Tax Invoice',
                reference: invoice?.reference ?? null,
                status: invoice?.status ?? null,
                href: invoice ? `/customer_invoices/${invoice.id}/show` : null,
                icon: Package,
                color: 'orange',
            },
        ];

        return { projectId, projectName: project.name, stages };
    });

    // Only show projects that have at least one document
    const activePipelines = pipelines.filter((p) => p.stages.some((s) => s.reference));

    if (activePipelines.length === 0) {
        return (
            <Card className="border-none shadow-sm">
                <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                        <GitBranch className="w-3.5 h-3.5" /> Document Pipeline
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col items-center justify-center py-12 text-slate-300 gap-3">
                        <Truck className="w-10 h-10" />
                        <p className="text-sm font-medium">No active pipeline documents found.</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border-none shadow-sm">
            <CardHeader className="pb-3 border-b">
                <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <GitBranch className="w-3.5 h-3.5" /> Document Pipeline
                    <Badge variant="outline" className="ml-auto text-[10px] bg-muted">
                        {activePipelines.length} active projects
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-2 divide-y max-h-[420px] overflow-y-auto">
                {activePipelines.map((pipeline) => (
                    <div key={pipeline.projectId} className="py-4">
                        <p className="text-xs font-bold text-foreground mb-4 flex items-center gap-1.5">
                            <Circle className="w-2 h-2 fill-blue-500 text-blue-500" />
                            {pipeline.projectName}
                        </p>
                        <div className="flex items-start gap-1 flex-wrap">
                            {pipeline.stages.map((stage, i) => (
                                <div key={stage.label} className="flex items-start">
                                    <StageNode stage={stage} />
                                    {i < pipeline.stages.length - 1 && (
                                        <Connector filled={!!stage.reference && !!pipeline.stages[i + 1].reference} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};
